/**
 * SecondVisitQuestion - the same-day visit refusal, asked instead of
 * shown. The server refuses a second SPICE visit note for the same
 * client, day and band by default (duplicate_spice_visit); a second
 * visit can be real, so every surface that posts a visit note (the
 * Email helper's visit block, the note composer) puts this question
 * where the refusal would have been an error: post the same note again
 * carrying second_visit_confirmed, or keep the first note. One sentence
 * and one pair of actions, so the question reads the same wherever a
 * therapist meets it; only the confirming verb is the host's own.
 */

import type { ReactNode } from 'react';
import { Button } from '@/components/primitives/Button';
import { TextAction } from '@/components/workspace/TextAction';

export const SECOND_VISIT_QUESTION =
  'A visit note is already on file for this client today. File a second visit anyway?';

export interface SecondVisitQuestionProps {
  /** The confirming action's label, in the host's own verb: "File a
   * second visit" on the Email helper, "Post a second visit" on the
   * composer. */
  confirmLabel: string;
  /** Re-posts the same note with second_visit_confirmed: true. */
  onConfirm: () => void;
  /** Puts the question away; nothing more is posted. */
  onKeep: () => void;
  testIds: {
    question: string;
    confirm: string;
    keep: string;
  };
}

export function SecondVisitQuestion({
  confirmLabel,
  onConfirm,
  onKeep,
  testIds,
}: SecondVisitQuestionProps): ReactNode {
  return (
    <div
      role="status"
      className="space-y-3 rounded-[12px] border border-warm-200 bg-gold-soft/30 p-3"
      data-testid={testIds.question}
    >
      <p className="text-sm text-ink">{SECOND_VISIT_QUESTION}</p>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" onClick={onConfirm} data-testid={testIds.confirm}>
          {confirmLabel}
        </Button>
        <TextAction onClick={onKeep} data-testid={testIds.keep}>
          Keep the first note
        </TextAction>
      </div>
    </div>
  );
}
